import {
  AiChatConversation,
  AiChatTap,
} from '@euler/app/flows/ai-suite/conversation';
import { BiAnswer, BiAnswerAction, biAnswerText } from '@euler/model/bi/answer';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { BehaviorSubject, Subject, concatMap } from 'rxjs';

// Every turn on a dashboard goes to the BI agent; nothing hands off.

/**
 * The dashboard's own conversation: every turn carries the `bi_dashboard`
 * page (on a card's page, `bi_card`, so "this chart" is that card), and
 * the assistant answers about this dashboard and proposes changes to it.
 * Opened the first time the chat is shown.
 */
export function useBiDashboardChat(
  dashboardId: string,
  /** A tap that may have changed the dashboard settled: read it again. */
  onSettled: (outcome: 'changed' | 'undone') => Promise<void>,
  /** The card whose page the chat is on. */
  cardId?: string,
) {
  const { aiAssistantService } = useServiceFactory();
  const conversation = useMemo(
    () =>
      new AiChatConversation(
        aiAssistantService,
        undefined,
        cardId
          ? { page: 'bi_card', params: { dashboardId, cardId } }
          : { page: 'bi_dashboard', params: { dashboardId } },
      ),
    [aiAssistantService, dashboardId, cardId],
  );
  useEffect(() => () => conversation.dispose(), [conversation]);
  // The chat's cards hear of a tap only once the dashboard has read itself
  // again: a card that stops drawing its preview first would show the old
  // dashboard, with the cards a removal took, for a moment.
  const tapSettled$ = useCreation(() => new Subject<AiChatTap>(), []);
  useEffect(() => {
    const subscription = conversation.tapSettled$
      .pipe(
        concatMap(async tap => {
          if (
            tap.kind === 'bi' &&
            tap.answer.action !== BiAnswerAction.DiscardChanges
          )
            await onSettled(
              tap.answer.action === BiAnswerAction.Undo ? 'undone' : 'changed',
            );
          return tap;
        }),
      )
      .subscribe(tap => tapSettled$.next(tap));
    return () => subscription.unsubscribe();
  }, [conversation, onSettled, tapSettled$]);
  const [state] = useBehaviorSubject(conversation.state$);
  const visible$ = useCreation(() => new BehaviorSubject(false), []);
  const [visible, setVisible] = useBehaviorSubject(visible$);
  const opened = !!state.sessionId || state.loading || !!state.loadError;
  // Each time it is shown without its conversation (never opened, or the
  // last try failed), it opens it.
  useEffect(() => {
    const { sessionId, loading, loadError } = conversation.state$.getValue();
    if (visible && !loading && (!sessionId || loadError))
      void conversation.openPage();
  }, [visible, conversation]);

  /** A dashboard tap sent from outside the chat, e.g. the preview banner. */
  const answer = useCallback(
    (value: BiAnswer) =>
      conversation.send(biAnswerText(value), [], { kind: 'bi', answer: value }),
    [conversation],
  );
  return {
    conversation,
    state,
    tapSettled$,
    /** Shown at least once: it stays mounted, so its preview stays. */
    opened,
    visible,
    setVisible,
    answer,
  };
}
